import { useEffect, useRef, useState } from 'react';
import { HOME } from '@/data/home';

/**
 * The three stacked words keep their own slot each — Justice / Technology /
 * Accountability have different weights and positions — so they are read by
 * index, with the bundled word standing in if the CMS list is short.
 */
const word = (i: number, fallback: string): string => HOME.hero.words[i]?.trim() || fallback;
import { Link } from 'react-router';
import { useMotion, useViewportProgress } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import { HERO_PROJECTS } from '@/data/projects';

// WebP, ~33 KB (the source PNG was 1.6 MB for the same 1920×1105 frame). Preloaded
// at high priority from index.html so it is decoded before the reveal starts.
const HERO_BG = '/landing/hero/hero-bg.webp';
const ARROW_IDLE = '/landing/hero/arrow-idle.svg';
const ARROW_ACTIVE = '/landing/hero/arrow-active.svg';
const GRADIENT_LINE = '/landing/hero/gradient-line.svg';
const AVATARS = ['/landing/hero/avatar1.webp', '/landing/hero/avatar2.webp', '/landing/hero/avatar3.webp'];
const AVATAR_PLUS = '/landing/hero/avatar4.svg';

/** Resolve `delay` seconds into an inline animation-delay style. */
const at = (delay: number): React.CSSProperties => ({ animationDelay: `${delay}s` });

const Dot = ({ className, delay }: { className: string; delay: number }) => (
    <div
        className={`cg-intro cg-intro--dot absolute size-[24px] rounded-full bg-[#f60] ${className}`}
        style={at(delay)}
    />
);

/**
 * The hero builds in layers once the preloader hands off (or immediately on a
 * repeat visit that skips it): background → "Justice" → "Technology" → stats →
 * CTA → project list (item-by-item) → "Accountability" last. Drives the
 * `.hero-in` switch that arms the `.cg-intro--*` keyframes (see globals.css).
 */
function useHeroReady(): boolean {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') {
            setReady(true);
            return;
        }
        // No preloader this visit → reveal on the next frame.
        if (!window.__cgPreloading) {
            const id = requestAnimationFrame(() => setReady(true));
            return () => cancelAnimationFrame(id);
        }
        const onReveal = () => setReady(true);
        window.addEventListener('cg:reveal-hero', onReveal);
        return () => window.removeEventListener('cg:reveal-hero', onReveal);
    }, []);
    return ready;
}

/**
 * Track which row currently reads as "active": the one scrolled to the leading
 * edge of the list. Derived from scroll position rather than held separately, so
 * the highlight follows the auto-advance AND a manual scroll or swipe.
 */
function useActiveRow(ref: React.RefObject<HTMLDivElement | null>, axis: 'y' | 'x'): number {
    const [active, setActive] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const sync = () => {
            const rows = el.children as HTMLCollectionOf<HTMLElement>;
            if (rows.length < 2) return;
            // Measured live, so it is correct even if the list mounted while hidden
            // (e.g. below the lg breakpoint) and was shown later.
            const step = axis === 'y' ? rows[1].offsetTop - rows[0].offsetTop : rows[1].offsetLeft - rows[0].offsetLeft;
            if (step <= 0) return;
            const offset = axis === 'y' ? el.scrollTop : el.scrollLeft;
            setActive(Math.min(rows.length - 1, Math.max(0, Math.round(offset / step))));
        };

        sync();
        el.addEventListener('scroll', sync, { passive: true });
        return () => el.removeEventListener('scroll', sync);
    }, [ref, axis]);

    return active;
}

/**
 * "Our Top Projects" — a self-contained, vertically scrollable list (desktop).
 * The list fills the height of the gradient divider beside it and scrolls when the
 * projects overflow. Each row builds in with the hero's layered reveal and, on
 * hover, brightens, nudges right, zooms its thumbnail and cross-fades its arrow.
 */
function HeroTopProjects() {
    const listRef = useRef<HTMLDivElement>(null);
    const active = useActiveRow(listRef, 'y');
    // Pointing at a row makes it the active one, so hovering a dimmed row lights
    // that row rather than leaving the auto-advance's choice highlighted too.
    const [hovered, setHovered] = useState<number | null>(null);
    const current = hovered ?? active;

    // Auto-advance the list one project every 3s (smooth), looping back to the
    // top at the end. Pauses on hover and honours reduced-motion.
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        let paused = false;
        const onEnter = () => (paused = true);
        const onLeave = () => (paused = false);
        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);

        const id = setInterval(() => {
            if (paused) return;
            const rows = el.children as HTMLCollectionOf<HTMLElement>;
            // Compute the row pitch live so it's correct even if the list mounted
            // while hidden (e.g. below the lg breakpoint) and was shown later.
            const step = rows.length > 1 ? rows[1].offsetTop - rows[0].offsetTop : 0;
            const maxScroll = el.scrollHeight - el.clientHeight;
            if (step <= 0 || maxScroll <= 0) return;
            // Read the position off the element rather than a counter of our own,
            // so scrolling the list by hand doesn't leave the next tick stale.
            let top = (Math.round(el.scrollTop / step) + 1) * step;
            if (top > maxScroll + 1) top = 0;
            el.scrollTo({ top, behavior: 'smooth' });
        }, 3000);

        return () => {
            clearInterval(id);
            el.removeEventListener('mouseenter', onEnter);
            el.removeEventListener('mouseleave', onLeave);
        };
    }, []);

    return (
        <div className="absolute left-[1240px] top-0 flex w-[408px] flex-wrap content-start items-start justify-start gap-x-[17px] gap-y-[28px]">
            {/* Heading */}
            <div className="cg-intro cg-intro--slide relative h-[48px] w-[338px]" style={at(2.45)}>
                <p className="absolute left-0 top-[24px] -translate-y-1/2 whitespace-nowrap text-[40px] font-light leading-[48px] tracking-[-0.5px] text-white">
                    {HOME.hero.railHeading}
                </p>
                <div className="absolute left-[331px] top-[14px] h-[26px] w-[7px] bg-[#f60]" />
            </div>

            {/* Scrollable project list (scrollbar hidden, auto-advancing) */}
            <div
                ref={listRef}
                className="scrollbar-hide flex h-[360px] w-[389px] flex-col gap-[42px] overflow-y-auto scroll-smooth pr-[12px]"
            >
                {HERO_PROJECTS.map((p, i) => {
                    const isCurrent = i === current;
                    return (
                        <div key={p.slug} className="cg-intro cg-intro--slide" style={at(2.55 + i * 0.12)}>
                            <Link
                                to={`/projects/${p.slug}`}
                                onMouseEnter={() => setHovered(i)}
                                onMouseLeave={() => setHovered(null)}
                                onFocus={() => setHovered(i)}
                                onBlur={() => setHovered(null)}
                                aria-current={isCurrent ? 'true' : undefined}
                                className={`flex w-full items-center gap-[19px] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                    isCurrent ? 'translate-x-[8px] opacity-100' : 'opacity-35'
                                }`}
                            >
                                <div className="flex h-[89px] w-[108px] shrink-0 items-center justify-center overflow-hidden rounded-[19px] bg-white p-[18px]">
                                    <img
                                        src={p.logo}
                                        alt=""
                                        className={`size-full object-contain transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                            isCurrent ? 'scale-110' : ''
                                        }`}
                                    />
                                </div>
                                <p className="w-[176px] shrink-0 text-[20px] font-semibold leading-[28px] text-white">
                                    {p.name}
                                </p>
                                <span className="relative size-[40px] shrink-0">
                                    <img
                                        src={ARROW_IDLE}
                                        alt=""
                                        className={`absolute inset-0 size-[40px] transition-opacity duration-300 ${
                                            isCurrent ? 'opacity-0' : 'opacity-100'
                                        }`}
                                    />
                                    <img
                                        src={ARROW_ACTIVE}
                                        alt=""
                                        className={`absolute inset-0 size-[40px] transition-opacity duration-300 ${
                                            isCurrent ? 'opacity-100' : 'opacity-0'
                                        }`}
                                    />
                                </span>
                            </Link>
                        </div>
                    );
                })}
            </div>

            {/* Gradient divider line */}
            <img
                src={GRADIENT_LINE}
                alt=""
                className="cg-intro cg-intro--fade h-[360px] w-[2px] shrink-0"
                style={at(2.6)}
            />
        </div>
    );
}

function HeroDesktop({ ready }: { ready: boolean }) {
    const frameRef = useRef<HTMLDivElement>(null);
    const bgRef = useRef<HTMLImageElement>(null);
    const progress = useViewportProgress(frameRef);

    // Subtle vertical parallax on the (scaled-up) background while scrolling.
    useMotion(bgRef, progress, (el, p) => {
        el.style.transform = `translate3d(0, ${(p - 0.5) * 90}px, 0)`;
    });

    return (
        <FigmaFrame designWidth={1920} designHeight={1105} className="w-full">
            <div
                ref={frameRef}
                className={`relative h-[1105px] w-[1920px] bg-[#0d0d0d] text-white ${ready ? 'hero-in' : ''}`}
            >
                {/* Background image (natural orientation: arch left, scale centre-right).
                    The group scales from small to full-screen for the intro grow. */}
                <div className="cg-bg-reveal absolute left-0 top-0 h-[1105px] w-[1920px]">
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex-none">
                            <div className="relative h-[1105px] w-[1920px]">
                                <img
                                    ref={bgRef}
                                    src={HOME.hero.background || HERO_BG}
                                    alt=""
                                    fetchPriority="high"
                                    decoding="async"
                                    className="pointer-events-none absolute inset-0 size-full max-w-none scale-110 object-cover will-change-transform"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content group */}
                <div className="absolute left-[134px] top-[198px] h-[719px] w-[1648px]">
                    {/* Our Top Projects (scrollable) */}
                    <HeroTopProjects />

                    {/* Discover button */}
                    <Link
                        to={HOME.hero.ctaHref || '/projects'}
                        className="cg-intro cg-intro--rise absolute left-[4px] top-[calc(50%+337.5px)] flex w-[216px] -translate-y-1/2 items-center justify-center rounded-[10px] bg-[#f60] px-[20px] py-[10px] text-[16px] font-bold leading-[24px] text-white transition-transform duration-300 hover:scale-[1.03]"
                        style={at(2.55)}
                    >
                        {HOME.hero.ctaLabel}
                    </Link>

                    {/* Paragraph */}
                    <p
                        className="cg-intro cg-intro--fade absolute left-[4px] top-[604.5px] h-[73px] w-[693px] -translate-y-1/2 text-[18px] font-normal leading-[28px] text-white"
                        style={at(2.5)}
                    >
                        {HOME.hero.paragraph}
                    </p>

                    {/* 12M+ stat */}
                    <div
                        className="cg-intro cg-intro--rise absolute left-[220px] top-[388px] flex items-end gap-[8px] whitespace-nowrap text-white"
                        style={at(2.4)}
                    >
                        <span className="text-[40px] font-bold leading-[48px] tracking-[-0.5px]">
                            {HOME.hero.statValue}
                        </span>
                        <span className="text-[18px] font-normal leading-[28px]">{HOME.hero.statLabel}</span>
                    </div>

                    {/* Accountability — starts shifted left, slides right into place as the bg fills */}
                    <p
                        className="cg-intro cg-word--l absolute left-[881px] top-[643px] -translate-y-1/2 whitespace-nowrap text-[110px] font-light leading-[108px] text-white"
                        style={at(0.45)}
                    >
                        {word(2, 'Accountability')}
                    </p>
                    <Dot className="left-[1622px] top-[685px]" delay={2.5} />

                    {/* Justice — starts shifted right, slides left into place as the bg fills */}
                    <p
                        className="cg-intro cg-word--r absolute left-0 top-[102px] -translate-y-1/2 whitespace-nowrap text-[110px] font-normal leading-[108px] text-white"
                        style={at(0.25)}
                    >
                        {word(0, 'Justice')}
                    </p>
                    <Dot className="left-[368px] top-[125px]" delay={2.3} />

                    {/* Technology */}
                    <p
                        className="cg-intro cg-word--r absolute left-[4px] top-[244px] -translate-y-1/2 whitespace-nowrap text-[110px] font-bold italic leading-[108px] text-[#e0e0e0]"
                        style={at(0.3)}
                    >
                        {word(1, 'Technology')}
                    </p>
                    <Dot className="left-[621px] top-[274px]" delay={2.4} />

                    {/* Avatars */}
                    <img
                        src={AVATARS[0]}
                        alt=""
                        className="cg-intro cg-intro--rise absolute left-[4px] top-[383px] size-[58px]"
                        style={at(2.4)}
                    />
                    <img
                        src={AVATARS[1]}
                        alt=""
                        className="cg-intro cg-intro--rise absolute left-[49.34px] top-[383px] h-[58px] w-[59.55px]"
                        style={at(2.47)}
                    />
                    <img
                        src={AVATARS[2]}
                        alt=""
                        className="cg-intro cg-intro--rise absolute left-[96.25px] top-[383px] h-[58px] w-[59.55px]"
                        style={at(2.54)}
                    />
                    <img
                        src={AVATAR_PLUS}
                        alt=""
                        className="cg-intro cg-intro--rise absolute left-[143.16px] top-[383px] size-[59px]"
                        style={at(2.61)}
                    />
                </div>
            </div>
        </FigmaFrame>
    );
}

function HeroMobile({ ready }: { ready: boolean }) {
    const railRef = useRef<HTMLDivElement>(null);
    const active = useActiveRow(railRef, 'x');

    return (
        <section
            className={`relative flex min-h-dvh flex-col overflow-hidden bg-[#0d0d0d] text-white ${ready ? 'hero-in' : ''}`}
        >
            {/* Background image (natural orientation). The group scales from small
                to full-screen for the intro grow. */}
            <div className="cg-bg-reveal absolute inset-0">
                <img
                    src={HOME.hero.background || HERO_BG}
                    alt=""
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 size-full object-cover"
                />
            </div>

            {/* Legibility wash over the photo */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage:
                        'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 38%, rgba(0,0,0,0.72) 100%)',
                }}
            />

            {/* Content is bottom-anchored: the block sits at the base of the frame
                and any slack falls above it, under the navbar. */}
            <div className="relative flex flex-1 flex-col justify-end px-[22px] pb-[30px] pt-[100px]">
                {/* Avatars + stat — sits above the headlines */}
                <div className="cg-intro cg-intro--rise flex items-center gap-[12px]" style={at(2.4)}>
                    <div className="flex shrink-0">
                        <img src={AVATARS[0]} alt="" className="size-[36px]" />
                        <img src={AVATARS[1]} alt="" className="-ml-[9px] size-[36px]" />
                        <img src={AVATARS[2]} alt="" className="-ml-[9px] size-[36px]" />
                    </div>
                    <div className="flex flex-col gap-[1px] whitespace-nowrap">
                        <span className="text-[18px] font-bold leading-[22px] tracking-[-0.5px] text-white">
                            {HOME.hero.statValue}
                        </span>
                        <span className="text-[13px] font-normal leading-[16px] text-[#d5d5d5]">
                            {HOME.hero.statLabel}
                        </span>
                    </div>
                </div>

                {/* Headlines — Justice / Technology / Accountability stacked */}
                <h1 className="mt-[26px] flex flex-col gap-[10px] font-primary text-white">
                    <span
                        className="cg-intro cg-word--r relative w-fit pr-[16px] text-[56px] font-normal leading-[1.06] tracking-[-0.02em]"
                        style={at(0.25)}
                    >
                        {word(0, 'Justice')}
                        <span className="absolute bottom-[11px] right-0 size-[11px] rounded-full bg-[#f60]" />
                    </span>
                    <span
                        className="cg-intro cg-word--r relative w-fit pr-[16px] text-[56px] font-bold italic leading-[1.06] tracking-[-0.02em] text-[#e0e0e0]"
                        style={at(0.3)}
                    >
                        {word(1, 'Technology')}
                        <span className="absolute bottom-[11px] right-0 size-[11px] rounded-full bg-[#f60]" />
                    </span>
                    <span
                        className="cg-intro cg-word--r relative w-fit pr-[15px] text-[48px] font-normal leading-[1.08] tracking-[-0.02em]"
                        style={at(0.45)}
                    >
                        {word(2, 'Accountability')}
                        <span className="absolute bottom-[9px] right-0 size-[10px] rounded-full bg-[#f60]" />
                    </span>
                </h1>

                {/* Paragraph */}
                <p
                    className="cg-intro cg-intro--fade mt-[24px] text-[14px] font-normal leading-[26px] text-[#d5d5d5]"
                    style={at(2.5)}
                >
                    We pair legal expertise with open data and technology to move stalled cases forward and put real
                    help within reach.
                </p>

                {/* Our Top Projects */}
                <div className="cg-intro cg-intro--fade mt-[46px]" style={at(2.55)}>
                    <div className="mb-[18px] flex items-center gap-[10px]">
                        <span className="h-[18px] w-[3px] bg-[#f60]" />
                        <h2 className="text-[15px] font-normal leading-[24px] tracking-[-0.3px] text-white">
                            {HOME.hero.railHeading}
                        </h2>
                    </div>
                    <div
                        ref={railRef}
                        className="scrollbar-hide -mx-[22px] flex snap-x snap-mandatory gap-[24px] overflow-x-auto px-[22px] pb-[2px]"
                    >
                        {HERO_PROJECTS.map((p, i) => {
                            const isCurrent = i === active;
                            return (
                                <Link
                                    key={p.slug}
                                    to={`/projects/${p.slug}`}
                                    aria-current={isCurrent ? 'true' : undefined}
                                    className={`flex shrink-0 snap-start items-center gap-[12px] transition-opacity duration-300 ${
                                        isCurrent ? 'opacity-100' : 'opacity-35'
                                    }`}
                                >
                                    <div className="flex h-[55px] w-[66px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-white p-[11px]">
                                        <img src={p.logo} alt="" className="size-full object-contain" />
                                    </div>
                                    <p className="w-[136px] shrink-0 text-[12px] font-bold leading-[19px] text-white">
                                        {p.name}
                                    </p>
                                    <img
                                        src={isCurrent ? ARROW_ACTIVE : ARROW_IDLE}
                                        alt=""
                                        className="size-[28px] shrink-0"
                                    />
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Discover button — 24px below the projects row */}
                <div className="mt-[24px] shrink-0">
                    <Link
                        to={HOME.hero.ctaHref || '/projects'}
                        className="cg-intro cg-intro--rise flex h-[48px] w-full items-center justify-center rounded-[10px] bg-[#f60] text-[16px] font-bold leading-[24px] text-white"
                        style={at(2.6)}
                    >
                        {HOME.hero.ctaLabel}
                    </Link>
                </div>
            </div>
        </section>
    );
}

export default function Hero() {
    const ready = useHeroReady();
    return (
        <>
            <div className="hidden lg:block">
                <HeroDesktop ready={ready} />
            </div>
            <div className="lg:hidden">
                <HeroMobile ready={ready} />
            </div>
        </>
    );
}
